import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import styles from '../styles.module.css';
import { locationSchema } from './utils';
import ChartIcon from '@/assets/icons/chart.svg?react';
import ErrorIcon from '@/assets/icons/error-toast.svg?react';
import PowerSiteIcon from '@/assets/icons/power-site.svg?react';
import {
  AdminCardButtons,
  AdminCardForm,
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import { Button } from '@/components/Form/Button';
import DateInput from '@/components/Form/DateInput';
import Input from '@/components/Form/Input';
import InputSearch from '@/components/Form/InputSearch';
import { SearchResult } from '@/components/Form/InputSearch/types';
import Loader from '@/components/Form/Loader';
import RadioButton from '@/components/Form/RadioButton';
import Select from '@/components/Form/Select';
import DataPoint from '@/components/LocationDetails/dataPoint';
import { stateOptions } from '@/constants/locationsStates';
import useModal from '@/context/ModalContext/useModal';
import { useToast } from '@/context/ToastContext';
import useSearchCompanies from '@/hooks/services/Companies';
import {
  LocationFormData,
  useCreateUpdateLocation,
  useGetDataPoints
} from '@/hooks/services/Locations';

const radioOptions = {
  active: 'Active',
  inactive: 'Inactive'
};

const NewLocation = () => {
  const { showTypedModal, closeModal } = useModal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    customer: '',
    numberOfTurbines: '',
    address: '',
    city: '',
    nearestCity: '',
    state: 'Texas',
    latitude: '',
    longitude: '',
    status: 'Active',
    startDate: new Date().toISOString().slice(0, 10),
    dataPoints: {}
  });
  const [searchCompanyInput, setSearchCompanyInput] = useState('');
  const [companyResults, setCompanyResults] = useState<SearchResult[]>([]);
  const { showToast } = useToast();

  const { data: dataCompanies, isFetching: isFetchingCompanies } =
    useSearchCompanies(searchCompanyInput, {
      useQueryOptions: {
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: searchCompanyInput.length > 0
      }
    });

  useEffect(() => {
    const companiesResults = dataCompanies?.data || [];
    setCompanyResults(companiesResults);
  }, [dataCompanies]);

  const onSearch = (query: string) => {
    if (query) {
      setSearchCompanyInput(query);
    } else {
      setCompanyResults([]);
    }
  };

  const onSelect = (item: SearchResult) => {
    handleSelectFormData([
      { field: 'company', value: item.name },
      { field: 'companyId', value: item.id }
    ]);
    setCompanyResults([]);
  };

  const onReset = () => {
    handleSelectFormData([
      { field: 'company', value: '' },
      { field: 'companyId', value: '' }
    ]);
    setSearchCompanyInput('');
    setCompanyResults([]);
  };

  const { mutate: saveLocation, isPending: isSaving } =
    useCreateUpdateLocation('create');
  const {
    data: dataPointsData,
    isLoading: isLoadingDataPoints,
    isFetching: isFetchingDataPoints,
    isError: isErrorDataPoints,
    error: errorDataPoints,
    refetch: refetchDataPoints
  } = useGetDataPoints({
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  });
  const dataPointsLabels = dataPointsData?.labels;

  useEffect(() => {
    if (!dataPointsLabels) {
      return;
    }

    setFormData(prev => {
      if (prev.dataPoints && Object.keys(prev.dataPoints).length > 0) {
        return prev;
      }

      const initialDataPoints = Object.keys(dataPointsLabels).reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>
      );

      return {
        ...prev,
        dataPoints: initialDataPoints
      };
    });
  }, [dataPointsLabels]);

  useEffect(() => {
    if (isErrorDataPoints) {
      const errorMessage =
        (errorDataPoints as Error)?.message ||
        'Something went wrong while loading data points.';

      showToast(errorMessage, {
        type: 'error',
        icon: ErrorIcon,
        filled: false,
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          refetchDataPoints();
        }
      });
    }
  }, [isErrorDataPoints, errorDataPoints, showToast, refetchDataPoints]);

  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});

  const handleCreateConfirmation = () => {
    showTypedModal({
      type: 'success',
      title: 'Location was successfully created'
    });
    setTimeout(() => {
      closeModal();
      navigate('/admin-tool/locations');
    }, 3000);
  };

  const handleCreateError = (error: string) => {
    showToast(error, {
      type: 'error',
      icon: ErrorIcon,
      filled: false,
      autoClose: 0
    });
  };

  const requiredFields = Object.keys(locationSchema.shape);

  const handleInputChange = (field: keyof LocationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const inputComponent = ({
    id,
    label,
    extraProps
  }: {
    id: keyof LocationFormData;
    label: string;
    extraProps?: any;
  }) => (
    <Input
      label={label}
      value={formData[id] as string}
      onChange={(value: string) => handleInputChange(id, value)}
      required={requiredFields.includes(id as string)}
      error={formErrors[id as string] || undefined}
      customClass={styles.adminInput}
      {...extraProps}
    />
  );

  const handleSelectFormData = (value: any) => {
    setFormData(prev => ({
      ...prev,
      customer: value[0].value
    }));
    if (formErrors.customer) {
      setFormErrors(prev => ({
        ...prev,
        customer: ''
      }));
    }
  };

  const handleDataPointChange = (
    dataPoint: keyof LocationFormData['dataPoints'],
    value: boolean
  ) => {
    setFormData(prev => ({
      ...prev,
      dataPoints: {
        ...prev.dataPoints,
        [dataPoint]: value
      }
    }));
  };

  const handleSave = () => {
    try {
      locationSchema.parse(formData);
      setFormErrors({});

      setIsLoading(true);
      saveLocation(formData, {
        onSuccess: () => {
          setIsLoading(false);

          handleCreateConfirmation();
        },
        onError: error => {
          console.error('Error saving location:', error);
          setIsLoading(false);
          handleCreateError(error.message);
        }
      });
    } catch (error: any) {
      if (error.errors) {
        const errors: { [k: string]: string } = {};
        error.errors.forEach((e: any) => {
          if (e.path[0]) errors[e.path[0].toString()] = e.message;
        });
        setFormErrors(errors);
      }
    }
  };

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <PowerSiteIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>New Location</h2>
            </>
          }
          right={
            <>
              <RadioButton
                checked={formData.status === 'Active'}
                onChange={() => handleInputChange('status', 'Active')}
                label={radioOptions.active}
              />
              <RadioButton
                checked={formData.status !== 'Active'}
                onChange={() => handleInputChange('status', 'Inactive')}
                label={radioOptions.inactive}
              />
            </>
          }
        />

        <AdminCardForm>
          {inputComponent({ id: 'name', label: 'Name' })}

          {inputComponent({ id: 'nearestCity', label: 'Nearest City' })}

          <InputSearch
            label="Customer"
            placeHolder="Search customer"
            defaultValue={formData?.customer}
            results={companyResults}
            process={isFetchingCompanies}
            onSearch={onSearch}
            onSelect={onSelect}
            onReset={onReset}
            required={requiredFields.includes('customer')}
            readOnly={false}
            error={formErrors.customer || undefined}
          />

          <Select
            label="State"
            value={formData.state}
            onChange={(value: string) => handleInputChange('state', value)}
            options={stateOptions.map(state => ({
              value: state,
              label: state
            }))}
            error={formErrors.state}
          />

          <DateInput
            label="Start Date"
            value={formData.startDate}
            onChange={(value: string) => handleInputChange('startDate', value)}
            error={!!formErrors.startDate}
          />

          {inputComponent({ id: 'latitude', label: 'Latitude' })}

          {inputComponent({ id: 'address', label: 'Address' })}

          {inputComponent({ id: 'longitude', label: 'Longitude' })}
        </AdminCardForm>
      </div>

      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <ChartIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>User Data Point Visibility</h2>
            </>
          }
        />

        <AdminTableBody>
          {isErrorDataPoints ? (
            <div className={styles.loadingContainer}>
              <span>Unable to load data points. Please try again.</span>
            </div>
          ) : isLoadingDataPoints || isFetchingDataPoints ? (
            <div className={styles.loadingContainer}>
              <Loader size="large" />
            </div>
          ) : (
            <DataPoint
              editMode={true}
              formData={formData}
              handleDataPointChange={handleDataPointChange}
              dataPointsLabels={dataPointsLabels || undefined}
            />
          )}
        </AdminTableBody>
      </div>

      <AdminCardButtons>
        <Button
          color="blue"
          solid={false}
          onClick={() => navigate('/admin-tool/locations')}
          disabled={isSaving}
          size="medium"
          label="CANCEL"
        />
        <Button
          onClick={handleSave}
          color="blue"
          label="SAVE"
          size="medium"
          loading={isLoading}
        />
      </AdminCardButtons>
    </div>
  );
};

export default NewLocation;
